import { useState, useContext, useEffect, useCallback } from "react";
import '../../styles/page-titles.css'
import PermissionGate from "../../services/PermissionGate";
import styled from 'styled-components';
import { SidebarContext } from "../../AppRoutes";
import { getRelatorioVendasProduto } from "../../services/api";
import { nowTz } from '../../utils/timezone';
import Loading from "react-loading";
import { FaBoxOpen, FaTrophy, FaMedal, FaAward, FaChartPie, FaLayerGroup, FaCheckCircle, FaExclamationTriangle, FaTimesCircle, FaLightbulb } from 'react-icons/fa';
import { MdTrendingUp, MdCategory, MdAnalytics } from 'react-icons/md';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { formatPriceWithSymbol } from '../../utils/currency';
import regionConfig from '../../config/regionConfig';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: ${({ sidebar }) => (sidebar ? '250px' : '100px')};
    min-height: 100vh;
    width: auto;
    transition: margin-left 200ms ease;
    background-color: #ffffff;
    padding: 24px;

    @media (max-width: 880px) {
        margin-left: 0;
        padding: 16px;
    }
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    flex-wrap: wrap;
    gap: 16px;

    .filters {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
    }

    input[type="date"] {
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 8px;
        font-size: 14px;
    }

    button {
        padding: 8px 16px;
        background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));
        color: white;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 500;
        transition: opacity 0.2s;

        &:hover {
            opacity: 0.9;
        }

        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
        }
    }

    @media (max-width: 600px) {
        h1 {
            font-size: 18px;
        }

        .filters {
            width: 100%;
        }

        input[type="date"], button {
            flex: 1;
        }
    }
`;

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 30px;

    @media (max-width: 500px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
    }
`;

const MetricCard = styled.div`
    background: white;
    padding: 20px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    display: flex;
    flex-direction: column;
    gap: 8px;

    .icon {
        width: 40px;
        height: 40px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        margin-bottom: 8px;
    }

    .icon.blue { background: rgba(49, 140, 213, 0.1); color: rgb(49, 140, 213); }
    .icon.gold { background: rgba(255, 193, 7, 0.1); color: #ffc107; }
    .icon.green { background: rgba(40, 167, 69, 0.1); color: #28a745; }
    .icon.purple { background: rgba(102, 126, 234, 0.1); color: #667eea; }

    .label {
        font-size: 14px;
        color: #666;
    }

    .value {
        font-size: 28px;
        font-weight: 700;
        color: #333;
    }

    .sub {
        font-size: 12px;
        color: #999;
    }

    @media (max-width: 500px) {
        padding: 14px;
        .value { font-size: 22px; }
    }
`;

const ChartsRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 30px;

    @media (max-width: 1000px) {
        grid-template-columns: 1fr;
    }
`;

const ChartCard = styled.div`
    background: white;
    padding: 24px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #333;
        margin-bottom: 20px;
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .chart-container {
        height: 300px;
        width: 100%;
    }

    @media (max-width: 600px) {
        padding: 16px;

        .chart-container {
            height: 250px;
        }
    }
`;

const FullWidthChart = styled(ChartCard)`
    grid-column: 1 / -1;

    .chart-container {
        height: 350px;
    }
`;

const TopProductsTable = styled.div`
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    margin-bottom: 20px;

    .table-header {
        padding: 20px 24px;
        border-bottom: 1px solid #e2e8f0;

        h3 {
            font-size: 18px;
            font-weight: 600;
            color: #2d3748;
            margin: 0;
            display: flex;
            align-items: center;
            gap: 10px;
        }
    }

    .table-wrapper {
        overflow-x: auto;
    }

    table {
        width: 100%;
        border-collapse: collapse;

        thead {
            background: linear-gradient(135deg, rgb(49, 140, 213), rgb(79, 172, 254));

            th {
                padding: 16px;
                text-align: left;
                font-weight: 600;
                font-size: 13px;
                color: white;
                white-space: nowrap;

                &:first-child {
                    padding-left: 24px;
                }

                &:last-child {
                    padding-right: 24px;
                }
            }
        }

        tbody {
            tr {
                border-bottom: 1px solid #f1f5f9;
                transition: background-color 0.2s ease;

                &:hover {
                    background: #f8fafc;
                }

                td {
                    padding: 14px 16px;
                    color: #2d3748;
                    font-size: 14px;

                    &:first-child {
                        padding-left: 24px;
                    }

                    .rank {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        font-weight: 600;
                    }

                    .rank-1 { color: #ffc107; }
                    .rank-2 { color: #adb5bd; }
                    .rank-3 { color: #cd7f32; }

                    .curve-badge {
                        display: inline-flex;
                        padding: 4px 10px;
                        border-radius: 20px;
                        font-size: 12px;
                        font-weight: 600;

                        &.A { background: #d4edda; color: #155724; }
                        &.B { background: #fff3cd; color: #856404; }
                        &.C { background: #f8d7da; color: #721c24; }
                    }

                    .valor {
                        font-weight: 600;
                        color: rgb(49, 140, 213);
                    }
                }
            }
        }
    }

    @media (max-width: 768px) {
        table {
            min-width: 600px;
        }
    }
`;

const ABCLegend = styled.div`
    display: flex;
    gap: 16px;
    justify-content: center;
    margin-top: 16px;
    flex-wrap: wrap;

    .legend-item {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
        color: #666;

        .dot {
            width: 12px;
            height: 12px;
            border-radius: 50%;
        }

        .dot.A { background: #28a745; }
        .dot.B { background: #ffc107; }
        .dot.C { background: #dc3545; }
    }
`;

const ABCDetailSection = styled.div`
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    margin-bottom: 20px;

    .section-header {
        padding: 20px 24px;
        border-bottom: 1px solid #e2e8f0;
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: wrap;
        gap: 16px;

        h3 {
            font-size: 18px;
            font-weight: 600;
            color: #2d3748;
            margin: 0;
            display: flex;
            align-items: center;
            gap: 10px;
        }

        .description {
            font-size: 13px;
            color: #718096;
            margin-top: 4px;
        }
    }

    .tabs-container {
        display: flex;
        gap: 0;
        padding: 0 24px;
        border-bottom: 2px solid #e2e8f0;
        background: #f8fafc;
    }

    .tab-button {
        padding: 14px 24px;
        border: none;
        background: transparent;
        font-size: 14px;
        font-weight: 600;
        color: #718096;
        cursor: pointer;
        position: relative;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        gap: 8px;

        .count-badge {
            background: #e2e8f0;
            color: #4a5568;
            padding: 2px 8px;
            border-radius: 10px;
            font-size: 12px;
            font-weight: 600;
        }

        &:hover {
            color: #2d3748;
        }

        &.active {
            color: #2d3748;

            &::after {
                content: '';
                position: absolute;
                bottom: -2px;
                left: 0;
                right: 0;
                height: 3px;
                border-radius: 2px 2px 0 0;
            }

            &.curva-a {
                color: #155724;
                .count-badge { background: #d4edda; color: #155724; }
                &::after { background: #28a745; }
            }

            &.curva-b {
                color: #856404;
                .count-badge { background: #fff3cd; color: #856404; }
                &::after { background: #ffc107; }
            }

            &.curva-c {
                color: #721c24;
                .count-badge { background: #f8d7da; color: #721c24; }
                &::after { background: #dc3545; }
            }
        }
    }

    .tab-content {
        padding: 20px 24px;
    }

    .recommendation-card {
        padding: 16px 20px;
        border-radius: 12px;
        margin-bottom: 20px;
        display: flex;
        align-items: flex-start;
        gap: 12px;

        &.curva-a {
            background: linear-gradient(135deg, rgba(40, 167, 69, 0.1), rgba(40, 167, 69, 0.05));
            border-left: 4px solid #28a745;
        }

        &.curva-b {
            background: linear-gradient(135deg, rgba(255, 193, 7, 0.15), rgba(255, 193, 7, 0.05));
            border-left: 4px solid #ffc107;
        }

        &.curva-c {
            background: linear-gradient(135deg, rgba(220, 53, 69, 0.1), rgba(220, 53, 69, 0.05));
            border-left: 4px solid #dc3545;
        }

        .icon {
            font-size: 24px;
            flex-shrink: 0;
        }

        .content {
            h4 {
                margin: 0 0 4px 0;
                font-size: 15px;
                font-weight: 600;
                color: #2d3748;
            }

            p {
                margin: 0;
                font-size: 13px;
                color: #718096;
                line-height: 1.5;
            }
        }
    }

    .products-list {
        display: grid;
        gap: 12px;
    }

    .product-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 14px 16px;
        background: #f8fafc;
        border-radius: 10px;
        transition: all 0.2s ease;

        &:hover {
            background: #edf2f7;
            transform: translateX(4px);
        }

        .product-info {
            display: flex;
            flex-direction: column;
            gap: 4px;

            .name {
                font-weight: 600;
                color: #2d3748;
                font-size: 14px;
            }

            .category {
                font-size: 12px;
                color: #718096;
            }
        }

        .product-stats {
            display: flex;
            gap: 24px;
            align-items: center;

            .stat {
                text-align: right;

                .value {
                    font-weight: 600;
                    color: #2d3748;
                    font-size: 14px;
                }

                .label {
                    font-size: 11px;
                    color: #a0aec0;
                    text-transform: uppercase;
                }
            }

            .percentage {
                background: #e2e8f0;
                padding: 4px 10px;
                border-radius: 20px;
                font-size: 12px;
                font-weight: 600;
                color: #4a5568;
                min-width: 60px;
                text-align: center;
            }
        }
    }

    .empty-state {
        text-align: center;
        padding: 40px 20px;
        color: #a0aec0;

        svg {
            font-size: 48px;
            margin-bottom: 16px;
            opacity: 0.5;
        }

        p {
            margin: 0;
            font-size: 14px;
        }
    }

    @media (max-width: 768px) {
        .tabs-container {
            padding: 0 16px;
            overflow-x: auto;
        }

        .tab-button {
            padding: 12px 16px;
            white-space: nowrap;
        }

        .tab-content {
            padding: 16px;
        }

        .product-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;

            .product-stats {
                width: 100%;
                justify-content: space-between;
            }
        }
    }
`;

const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
`;

const COLORS = ['#318CD5', '#4FACFE', '#7CC4FF', '#A8D8FF', '#D4ECFF', '#E8F4FF'];
const ABC_COLORS = { A: '#28a745', B: '#ffc107', C: '#dc3545' };

const RelatorioProdutos = () => {
    const empresa = localStorage.getItem('empresa');
    const empresaParse = JSON.parse(empresa);
    const idEmpresa = empresaParse.id_empresa;

    const { sidebar } = useContext(SidebarContext);
    const { t } = useTranslation('reports');

    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState({
        resumo: {
            total_itens_vendidos: 0,
            produtos_unicos: 0,
            faturamento_total: 0,
            ticket_medio: 0
        },
        top_faturamento: [],
        por_categoria: [],
        curva_abc: { classe_a: 0, classe_b: 0, classe_c: 0, produtos: [] }
    });
    const [startDate, setStartDate] = useState(nowTz().subtract(30, 'days').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(nowTz().format('YYYY-MM-DD'));
    const [activeABCTab, setActiveABCTab] = useState('C'); // Começar pela curva C (produtos para avaliar remoção)

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await getRelatorioVendasProduto(startDate, endDate);
            if (response?.data?.success) {
                setData(response.data);
            }
        } catch (error) {
            console.error('[RELATORIO-PRODUTOS] Erro ao buscar dados:', error);
            toast.error(t('common.fetchError'));
        } finally {
            setIsLoading(false);
        }
    }, [startDate, endDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Preparar dados para gráfico de categorias (Pie)
    const categoriasPieData = (data.por_categoria || []).slice(0, 6).map(cat => ({
        name: cat.categoria || t('common.noCategory'),
        value: cat.faturamento || 0,
        quantidade: cat.quantidade || 0
    }));

    // Preparar dados para curva ABC (Bar)
    const curvaAbcData = [
        { name: t('products.curveA'), produtos: data.curva_abc?.classe_a || 0, cor: ABC_COLORS.A },
        { name: t('products.curveB'), produtos: data.curva_abc?.classe_b || 0, cor: ABC_COLORS.B },
        { name: t('products.curveC'), produtos: data.curva_abc?.classe_c || 0, cor: ABC_COLORS.C }
    ];

    // Não há evolução de vendas no backend atual, remover ou deixar vazio
    const evolucaoData = [];

    // Separar produtos por curva ABC
    const produtosCurvaA = (data.curva_abc?.produtos || []).filter(p => p.classificacao === 'A');
    const produtosCurvaB = (data.curva_abc?.produtos || []).filter(p => p.classificacao === 'B');
    const produtosCurvaC = (data.curva_abc?.produtos || []).filter(p => p.classificacao === 'C');

    // Calcular totais por curva
    const faturamentoTotal = data.resumo?.faturamento_total || 1;
    const faturamentoCurvaA = produtosCurvaA.reduce((sum, p) => sum + (p.faturamento || 0), 0);
    const faturamentoCurvaB = produtosCurvaB.reduce((sum, p) => sum + (p.faturamento || 0), 0);
    const faturamentoCurvaC = produtosCurvaC.reduce((sum, p) => sum + (p.faturamento || 0), 0);

    // Produtos ativos na aba selecionada
    const produtosAtivoTab = activeABCTab === 'A' ? produtosCurvaA :
                             activeABCTab === 'B' ? produtosCurvaB : produtosCurvaC;

    const formatCurrency = (value) => {
        return formatPriceWithSymbol(value || 0);
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            // Verifica se o campo deve ser formatado como moeda
            const isCurrencyField = (name, dataKey) => {
                const currencyKeywords = ['faturamento', 'valor', 'value', 'total', 'ticket', 'preco', 'receita'];
                const lowerName = (name || '').toLowerCase();
                const lowerKey = (dataKey || '').toLowerCase();
                return currencyKeywords.some(kw => lowerName.includes(kw) || lowerKey.includes(kw));
            };

            return (
                <div style={{
                    background: 'white',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    border: '1px solid #e0e0e0'
                }}>
                    <p style={{ margin: 0, fontWeight: 600, color: '#333' }}>{label || payload[0]?.payload?.name}</p>
                    {payload.map((entry, index) => (
                        <p key={index} style={{ margin: '4px 0 0', color: entry.color || '#318CD5', fontSize: '13px' }}>
                            {entry.name}: {isCurrencyField(entry.name, entry.dataKey)
                                ? formatCurrency(entry.value)
                                : entry.value?.toLocaleString?.(regionConfig.currency.locale) || entry.value}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    const getRankIcon = (index) => {
        if (index === 0) return <FaTrophy color="#ffc107" />;
        if (index === 1) return <FaMedal color="#adb5bd" />;
        if (index === 2) return <FaAward color="#cd7f32" />;
        return null;
    };

    return (
        <>
            <PermissionGate permissions={['default']}>
                <Container sidebar={sidebar}>
                    <Header>
                        <div className="page-title-with-icon">
                            <div className="page-icon"><FaBoxOpen /></div>
                            <h1 className="page-title">{t('products.title')}</h1>
                        </div>
                        <div className="filters" style={{ marginRight: '50px' }}>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                            <button onClick={fetchData} disabled={isLoading}>
                                {isLoading ? t('common.updating') : t('common.update')}
                            </button>
                        </div>
                    </Header>

                    {isLoading ? (
                        <LoadingContainer>
                            <Loading type="spin" color="rgb(49, 140, 213)" height={40} width={40} />
                        </LoadingContainer>
                    ) : (
                        <>
                            <CardsGrid>
                                <MetricCard>
                                    <div className="icon blue">
                                        <FaBoxOpen />
                                    </div>
                                    <span className="label">{t('products.itemsSold')}</span>
                                    <span className="value">{data.resumo?.total_itens_vendidos || 0}</span>
                                    <span className="sub">{t('products.itemsSoldSub')}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon purple">
                                        <MdCategory />
                                    </div>
                                    <span className="label">{t('products.distinctProducts')}</span>
                                    <span className="value">{data.resumo?.produtos_unicos || 0}</span>
                                    <span className="sub">{t('products.distinctProductsSub')}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon green">
                                        <MdTrendingUp />
                                    </div>
                                    <span className="label">{t('common.revenue')}</span>
                                    <span className="value">
                                        {formatPriceWithSymbol(data.resumo?.faturamento_total || 0)}
                                    </span>
                                    <span className="sub">{t('products.revenueSub')}</span>
                                </MetricCard>

                                <MetricCard>
                                    <div className="icon gold">
                                        <FaTrophy />
                                    </div>
                                    <span className="label">{t('products.avgTicketProduct')}</span>
                                    <span className="value">
                                        {formatPriceWithSymbol(data.resumo?.ticket_medio || 0)}
                                    </span>
                                    <span className="sub">{t('products.avgTicketProductSub')}</span>
                                </MetricCard>
                            </CardsGrid>

                            <ChartsRow>
                                <ChartCard>
                                    <h3>
                                        <FaChartPie />
                                        {t('products.salesByCategory')}
                                    </h3>
                                    <div className="chart-container">
                                        {categoriasPieData.length > 0 ? (
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={categoriasPieData}
                                                        cx="50%"
                                                        cy="50%"
                                                        innerRadius={60}
                                                        outerRadius={100}
                                                        paddingAngle={3}
                                                        dataKey="value"
                                                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                                        labelLine={false}
                                                    >
                                                        {categoriasPieData.map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                        ))}
                                                    </Pie>
                                                    <Tooltip content={<CustomTooltip />} />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999' }}>
                                                {t('common.noData')}
                                            </div>
                                        )}
                                    </div>
                                </ChartCard>

                                <ChartCard>
                                    <h3>
                                        <FaLayerGroup />
                                        {t('products.abcCurve')}
                                    </h3>
                                    <div className="chart-container">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={curvaAbcData} layout="vertical">
                                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                                <XAxis type="number" />
                                                <YAxis type="category" dataKey="name" width={80} />
                                                <Tooltip content={<CustomTooltip />} />
                                                <Bar dataKey="produtos" fill="#318CD5" radius={[0, 8, 8, 0]}>
                                                    {curvaAbcData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.cor} />
                                                    ))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <ABCLegend>
                                        <div className="legend-item">
                                            <span className="dot A"></span>
                                            {t('products.abcLegendA')}
                                        </div>
                                        <div className="legend-item">
                                            <span className="dot B"></span>
                                            {t('products.abcLegendB')}
                                        </div>
                                        <div className="legend-item">
                                            <span className="dot C"></span>
                                            {t('products.abcLegendC')}
                                        </div>
                                    </ABCLegend>
                                </ChartCard>
                            </ChartsRow>

                            {evolucaoData.length > 0 && (
                                <FullWidthChart>
                                    <h3>
                                        <MdTrendingUp />
                                        {t('products.salesEvolution')}
                                    </h3>
                                    <div className="chart-container">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={evolucaoData}>
                                                <defs>
                                                    <linearGradient id="colorVendas" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#318CD5" stopOpacity={0.8}/>
                                                        <stop offset="95%" stopColor="#318CD5" stopOpacity={0.1}/>
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="data" />
                                                <YAxis yAxisId="left" orientation="left" />
                                                <YAxis yAxisId="right" orientation="right" tickFormatter={(value) => formatPriceWithSymbol(value)} />
                                                <Tooltip content={<CustomTooltip />} />
                                                <Legend />
                                                <Area
                                                    yAxisId="left"
                                                    type="monotone"
                                                    dataKey="vendas"
                                                    name={t('products.quantity')}
                                                    stroke="#318CD5"
                                                    fillOpacity={1}
                                                    fill="url(#colorVendas)"
                                                />
                                                <Area
                                                    yAxisId="right"
                                                    type="monotone"
                                                    dataKey="faturamento"
                                                    name={t('products.revenueChart')}
                                                    stroke="#28a745"
                                                    fill="transparent"
                                                />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </div>
                                </FullWidthChart>
                            )}

                            <TopProductsTable>
                                <div className="table-header">
                                    <h3>
                                        <FaTrophy color="#ffc107" />
                                        {t('products.top20Title')}
                                    </h3>
                                </div>
                                <div className="table-wrapper">
                                    <table>
                                        <thead>
                                            <tr>
                                                <th>{t('products.tableHeaders.position')}</th>
                                                <th>{t('products.tableHeaders.product')}</th>
                                                <th>{t('products.tableHeaders.category')}</th>
                                                <th>{t('products.tableHeaders.qtySold')}</th>
                                                <th>{t('products.tableHeaders.revenue')}</th>
                                                <th>{t('products.tableHeaders.abcCurve')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(data.curva_abc?.produtos || []).slice(0, 20).map((produto, index) => (
                                                <tr key={produto.id || index}>
                                                    <td>
                                                        <div className={`rank rank-${index + 1}`}>
                                                            {getRankIcon(index)}
                                                            #{index + 1}
                                                        </div>
                                                    </td>
                                                    <td>{produto.nome || t('products.noProductName')}</td>
                                                    <td>{produto.categoria || t('common.noCategory')}</td>
                                                    <td>{produto.quantidade}</td>
                                                    <td className="valor">
                                                        {formatPriceWithSymbol(produto.faturamento || 0)}
                                                    </td>
                                                    <td>
                                                        <span className={`curve-badge ${produto.classificacao || 'C'}`}>
                                                            {produto.classificacao || 'C'}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </TopProductsTable>

                            <ABCDetailSection>
                                <div className="section-header">
                                    <div>
                                        <h3>
                                            <MdAnalytics color="#318CD5" />
                                            {t('products.abcDetailTitle')}
                                        </h3>
                                        <p className="description">
                                            {t('products.abcDetailDesc')}
                                        </p>
                                    </div>
                                </div>

                                <div className="tabs-container">
                                    <button
                                        className={`tab-button curva-a ${activeABCTab === 'A' ? 'active' : ''}`}
                                        onClick={() => setActiveABCTab('A')}
                                    >
                                        <FaCheckCircle />
                                        {t('products.curveA')}
                                        <span className="count-badge">{produtosCurvaA.length}</span>
                                    </button>
                                    <button
                                        className={`tab-button curva-b ${activeABCTab === 'B' ? 'active' : ''}`}
                                        onClick={() => setActiveABCTab('B')}
                                    >
                                        <FaExclamationTriangle />
                                        {t('products.curveB')}
                                        <span className="count-badge">{produtosCurvaB.length}</span>
                                    </button>
                                    <button
                                        className={`tab-button curva-c ${activeABCTab === 'C' ? 'active' : ''}`}
                                        onClick={() => setActiveABCTab('C')}
                                    >
                                        <FaTimesCircle />
                                        {t('products.curveC')}
                                        <span className="count-badge">{produtosCurvaC.length}</span>
                                    </button>
                                </div>

                                <div className="tab-content">
                                    {activeABCTab === 'A' && (
                                        <div className="recommendation-card curva-a">
                                            <FaLightbulb className="icon" style={{ color: '#28a745' }} />
                                            <div className="content">
                                                <h4>{t('products.recA.title')}</h4>
                                                <p>
                                                    Estes {produtosCurvaA.length} produtos representam <strong>{((faturamentoCurvaA / faturamentoTotal) * 100).toFixed(1)}%</strong> do seu faturamento.
                                                    São os campeões de venda! Mantenha sempre em estoque, destaque no cardápio e considere criar combos.
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {activeABCTab === 'B' && (
                                        <div className="recommendation-card curva-b">
                                            <FaLightbulb className="icon" style={{ color: '#ffc107' }} />
                                            <div className="content">
                                                <h4>{t('products.recB.title')}</h4>
                                                <p>
                                                    Estes {produtosCurvaB.length} produtos representam <strong>{((faturamentoCurvaB / faturamentoTotal) * 100).toFixed(1)}%</strong> do faturamento.
                                                    Avalie promoções para aumentar vendas ou ajustes de preço. Alguns podem se tornar Curva A com estratégias certas.
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {activeABCTab === 'C' && (
                                        <div className="recommendation-card curva-c">
                                            <FaLightbulb className="icon" style={{ color: '#dc3545' }} />
                                            <div className="content">
                                                <h4>{t('products.recC.title')}</h4>
                                                <p>
                                                    Estes {produtosCurvaC.length} produtos representam apenas <strong>{((faturamentoCurvaC / faturamentoTotal) * 100).toFixed(1)}%</strong> do faturamento.
                                                    Considere remover do cardápio para simplificar operações, reduzir custos de estoque e focar nos produtos rentáveis.
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {produtosAtivoTab.length === 0 ? (
                                        <div className="empty-state">
                                            <FaBoxOpen />
                                            <p>{t('products.noProductsInCurve')}</p>
                                        </div>
                                    ) : (
                                        <div className="products-list">
                                            {produtosAtivoTab.map((produto, index) => (
                                                <div key={produto.id || index} className="product-item">
                                                    <div className="product-info">
                                                        <span className="name">{produto.nome || t('products.noProductName')}</span>
                                                        <span className="category">{produto.categoria || t('common.noCategory')}</span>
                                                    </div>
                                                    <div className="product-stats">
                                                        <div className="stat">
                                                            <div className="value">{produto.quantidade || 0}</div>
                                                            <div className="label">{t('products.sales')}</div>
                                                        </div>
                                                        <div className="stat">
                                                            <div className="value">
                                                                {formatPriceWithSymbol(produto.faturamento || 0)}
                                                            </div>
                                                            <div className="label">{t('common.revenue')}</div>
                                                        </div>
                                                        <div className="percentage">
                                                            {((produto.faturamento / faturamentoTotal) * 100).toFixed(1)}%
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </ABCDetailSection>
                        </>
                    )}
                </Container>
            </PermissionGate>
        </>
    );
};

export default RelatorioProdutos;
